
import React, { BaseSyntheticEvent, Fragment, useEffect, useRef, useState } from 'react';
import { CQAppConstant } from '../../../../../app-constants';
import { observer } from 'mobx-react';
import LayoutFieldsConfigController from '../../../layout-level-fields.controller';
import { ICriteria, IAction, IExtendedRule, ISetValuesRuleModalProps, ISetValuesRule } from '../../ILayoutConfigurationPanel.Interface';
import '../apply-rule-modal.css';
import { CQMasterDataStore } from '../../../../../store/cq-master-data.store';
import { CommonRuleModal } from '../common-rule-modal';
import { Utility } from '../../../../../common/utility';

const INITIAL_STATE = {
    field: '',
    operator: '',
    compareTo: '',
    isDynamic: false,
    selectedFieldType: '',
    referenceField: '',
    referenceTo: '',
    relationshipName: ''
}
const ACTION_INITIAL_STATE = {
    field: '',
    compareTo: '',
    isDynamic: false,
    supportedFunctionValue: 0,
    selectedFieldType: '',
}
const layoutFieldsConfigCtrl = new LayoutFieldsConfigController();
const utility = new Utility();

const generateSetValueInitialState = () => {

    const selectedField = {...CQMasterDataStore.Details.selectedFieldDetailsObject};
    return {
        ...utility.createObjectDeepCopy(INITIAL_STATE),
        field: selectedField.fullName,
        selectedFieldType: selectedField.type,
        referenceTo: selectedField.referenceTo || '',
        relationshipName: selectedField.relationshipName || ''
    }

}

const SetValuesRulesModalComponent = (props: ISetValuesRuleModalProps ) => {
    const [state, setState] = useState(() => ({
        setValues: [{
            addedCriterias: [generateSetValueInitialState()],
            actions: [ACTION_INITIAL_STATE],
            complexLogicQuery: '1',
            complexLogicVisibility: false,
            shouldSkip: false
        }]
    }))

    const [extraState, setExtraState] = useState<IExtendedRule>(() => ({
        additionalFields: [],
        fieldWisePicklistValues: [],
        selectedReferenceFields: [],
    }));

    const [shouldValidate, setShouldValidate] = useState(false);

    useEffect(() => {
        async  function intitializeState(){
            if (!props.appliedRule) {
                return;
            }
            const ruleObject = CQMasterDataStore.RuleElements.FieldRuleElement;
            const objName = props.objectName;
            const ruleElement = { ...(ruleObject[objName!])[props.appliedRule] };
            if (!ruleElement) {
                return;
            }
            const selectedObjectName = CQMasterDataStore.OutputConfig.getStoreKeyData(props.selectedTemplateName, 'selectedObject');
            let selectedReferenceFields: any[] | undefined = [];
            let fieldWisePicklistValues = ruleElement.fieldWisePicklistValues;
            if (ruleElement.selectedReferenceFields) {
                selectedReferenceFields = await layoutFieldsConfigCtrl.getLookFieldsDataInEditMode(ruleElement.selectedReferenceFields);
                selectedReferenceFields = (selectedReferenceFields && selectedReferenceFields.length > 0) ? selectedReferenceFields : [];
            }
            if (ruleElement.fieldWisePicklistValues) {
                fieldWisePicklistValues = await layoutFieldsConfigCtrl.getPickListValueDataInEditMode(selectedObjectName, ruleElement.fieldWisePicklistValues);
                fieldWisePicklistValues = (fieldWisePicklistValues && fieldWisePicklistValues.length > 0) ? fieldWisePicklistValues : [];
            }
            props.setRuleObject(ruleElement);
            const stateCopy = utility.createObjectDeepCopy(state);
            stateCopy.setValues = ruleElement.setValues;
            setState(stateCopy);
            setExtraState({
                selectedReferenceFields,
                fieldWisePicklistValues,
                additionalFields: ruleElement.additionalFields
            });
        }
        intitializeState();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        updateRuleObject(CQAppConstant.ADDED_CRITERIAS, generateMergedStore());
        props.checkValidation(validateAddRuleForm());

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    useEffect(() => {
        updateRuleObject(CQAppConstant.ADDED_CRITERIAS, generateMergedStore());

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extraState]);

    /**
     * @description Used to Merge store 
     * @memberof SetValuesRulesModalComponent
     */
    function generateMergedStore() {
        return { ...state, ...extraState };
    }

    /**
     * @description updates rule object and checks for validation for rule
     * @memberof SetValuesRulesModalComponent
     */
    function updateRuleObject(ruleObjectProperty: string, ruleObjectValue: any) {
        props.setRuleObject(ruleObjectValue);
        props.checkValidation(validateAddRuleForm());
    }

    /**
     * @description Used for validation for rule added for field type
     * @memberof SetValuesRulesModalComponent
     */
    function validateAddRuleForm() {

        if (!shouldValidate) {
            return true;
        } 
        
        let isComplexLogicValid = false;
        let isActionsValid = false;
        let isCriteriaValid = false
        let setValues = state.setValues;
        setValues.every((setValueSection) => {
            isCriteriaValid = setValueSection.addedCriterias.every((criteria: ICriteria, index: number) =>
            (criteria.field !== ''
                && criteria.operator !== ''
                && ((criteria.operator === CQAppConstant.EXISTS || criteria.operator === CQAppConstant.ISEMPTY) ? true : (criteria.compareTo !== ''))
                && ((CQAppConstant.REFERENCE_FIELD_TYPE).includes(criteria.selectedFieldType) ?
                    ((criteria.operator === CQAppConstant.EXISTS || criteria.operator === CQAppConstant.ISEMPTY || index === 0) ? true :
                        (criteria.referenceField !== '')) : true)
            ));

            if (setValueSection.shouldSkip) {
                isCriteriaValid = true;
            }
            isActionsValid = setValueSection.actions.every((action: any) => (
                action.field !== ''
                && action.compareTo !== ''));
            isComplexLogicValid = (setValueSection.complexLogicQuery === '');
            return (isCriteriaValid && isActionsValid && isComplexLogicValid);
        });

        if (!isCriteriaValid || !isActionsValid || isComplexLogicValid) {
            return true;
        }
        return false;
    }

    /**
     * @description Updates  fields for set value rule
     * @memberof SetValuesRulesModalComponent
     */
    function setValueRule(setValueSectionIndex: number) {
        const stateCopy = utility.createObjectDeepCopy(state);
        const updatedAdditionalFields = removeAllAdditionalFields(setValueSectionIndex);
        stateCopy.setValues = stateCopy.setValues.filter((setValueSection: any, index: number) => index !== setValueSectionIndex);
        setExtraState({
            selectedReferenceFields: [],
            fieldWisePicklistValues: [],
            additionalFields: updatedAdditionalFields
        });
        setState(stateCopy);
    }

    /**
     * @description Used to set value on Action value field
     * @param e event to set value for action value field
     * @memberof SetValuesRulesModalComponent
     */
    function onActionValueChanged(e: BaseSyntheticEvent, actionIndex: number, setValueSectionIndex: number, isToggleValue: boolean) {
        setValidation();
        const stateCopy = utility.createObjectDeepCopy(state);
        !isToggleValue ?
            stateCopy.setValues[setValueSectionIndex].actions[actionIndex].compareTo = e.target.value :
            stateCopy.setValues[setValueSectionIndex].actions[actionIndex].compareTo = e.target.checked;
        setState(stateCopy);
    }

    /**
     * @description Used to set value on Action field
     * @param e event to set value for action field
     * @memberof SetValuesRulesModalComponent
     */
    function onActionFieldChanged(e: BaseSyntheticEvent, actionIndex: number, setValueSectionIndex: number, layoutFields: any[]) {
        setValidation();
        const stateCopy = utility.createObjectDeepCopy(state);
        stateCopy.setValues[setValueSectionIndex].actions[actionIndex].field = e.target.value;
        const filteredField = layoutFields.filter((field: any) => field.fieldFullName === stateCopy.setValues[setValueSectionIndex].actions[actionIndex].field);
        stateCopy.setValues[setValueSectionIndex].actions[actionIndex].selectedFieldType = filteredField[0].fieldType;
        setState(stateCopy);
    }

    /**
     * @description returns 
     * @memberof SetValuesRulesModalComponent
     */
    function getTextInputTypeBasedOnFieldType(fieldType: string) {
        switch (fieldType) {
            case 'Number': return 'number';
                break;
            case 'Date': return 'date';
                break;
            case 'Text': return 'text';
                break;
            default: return 'text';
        }
    }

    /**
     * @description Removes additional field
     * @memberof SetValuesRulesModalComponent
     */
    function removeAllAdditionalFields(setValueSectionIndex: number) {
        const setValues = state.setValues;
        let additionalFields = [...extraState.additionalFields];
        const fields = setValues[setValueSectionIndex].addedCriterias.map((criteria: any) => criteria.field);
        fields.forEach((fieldName: string) => {
            additionalFields = additionalFields.filter((additionalField: string) => additionalField !== fieldName);
        });
        return additionalFields;
    }

    /**
     * @description  Updates value on  action support function value
     * @param e event to set value on action function 
     * @param actionIndex Index of action field
     * @param setValueSectionIndex Index of set value field
     * @memberof SetValuesRulesModalComponent
     */
    function onActionSupportedFuncValueChanged(e: BaseSyntheticEvent, actionIndex: number, setValueSectionIndex: number) {
        setValidation();
        const stateCopy = utility.createObjectDeepCopy(state);
        stateCopy.setValues[setValueSectionIndex].actions[actionIndex].supportedFunctionValue = e.target.value;
        setState(stateCopy);
    }

    /**
     * @description  renders dynamic field for action
     * @memberof SetValuesRulesModalComponent
     */
    function onDynamicActionChanged(e: BaseSyntheticEvent, actionIndex: number, setValueSectionIndex: number) {
        setValidation();
        const stateCopy = utility.createObjectDeepCopy(state);
        stateCopy.setValues[setValueSectionIndex].actions[actionIndex].isDynamic = e.target.checked;
        setState(stateCopy);
    }

    /**
     * @description add action field 
     * @memberof SetValuesRulesModalComponent
     */
    function addAction(setValueSectionIndex: number) {
        setValidation();
        const stateCopy = utility.createObjectDeepCopy(state);
        const action: any = {
            field: '',
            fieldType: '',
            compareTo: '',
            isDynamic: false
        };

        stateCopy.setValues[setValueSectionIndex].actions.push(action);
        setState(stateCopy);
    }

    /**
     * @description deletes the acton added
     * @memberof SetValuesRulesModalComponent
     */
    function deleteAction(actionIndex: number, setValueSectionIndex: number) {
        setValidation();
        const stateCopy = utility.createObjectDeepCopy(state);
        let actions = stateCopy.setValues[setValueSectionIndex].actions;
        actions = actions.filter((action: any, index: number) => index !== actionIndex);
        stateCopy.setValues[setValueSectionIndex].actions = actions;
        setState(stateCopy);
    }

    /**
     * @description adds set values in array 
     * @memberof SetValuesRulesModalComponent
     */
    function addSetValueSection() {   
        setValidation();   
        const stateCopy = utility.createObjectDeepCopy(state);
        const setValueSection ={
            addedCriterias: [generateSetValueInitialState()],
            actions: [ACTION_INITIAL_STATE],
            complexLogicQuery: '1',
            complexLogicVisibility: false,
            shouldSkip: false
        }
        stateCopy.setValues.push(setValueSection);
        setState(stateCopy);
    }

    /**
     * @description Used to set validation true for new states
     * @param newState contains state copy
     */
    function updateState (newState:ISetValuesRule) {
        setValidation();
        setState(newState);
    }

    /**
     * @description Used to validate rule
     */
    function setValidation() {
        if (!shouldValidate) {
            setShouldValidate(true);
        } 
    }

    return (
        <>
            <div className="slds-grid slds-grid_vertical">
                {
                    state.setValues && state.setValues.map((setValueSection: any, setValueSectionIndex: number) => {
                        return (
                            <React.Fragment key={setValueSectionIndex}>
                                <div className="slds-grid slds-grid_align-spread slds-grid_vertical-align-center slds-m-bottom_small">
                                    <h2>{`Rule ${setValueSectionIndex + 1}`} </h2>
                                    {setValueSectionIndex !== 0 &&
                                        <button className="slds-button slds-button_icon slds-button_icon-border-filled" onClick={() => {
                                            setValueRule(setValueSectionIndex);
                                        }}>
                                            <i className="cq-icon-delete"></i>
                                        </button>

                                    }
                                </div>

                                {
                                    <CommonRuleModal
                                        modalInfo={{
                                            shouldRenderAddComplexBtn: true,
                                            shouldDisableFirstElement: true,
                                            toggleModalVisibility: props.toggleModalVisibility
                                        }}
                                        parentAction={{
                                            updateState: (state: ISetValuesRule) => updateState(state),
                                            updateExtraState: (state: any) => setExtraState(state)
                                        }}
                                        parentInfo={{
                                            state: state.setValues,
                                            extraState,
                                            initialCriteria: INITIAL_STATE,
                                            sectionIndex: setValueSectionIndex,
                                            addFieldOnSkip: true,
                                        }}
                                        objectName={props.objectName}
                                        templateName={props.selectedTemplateName}
                                        selectedRule='setValues'
                                    />
                                }
                                <h3 className="slds-text-body_medium slds-m-vertical_small">Set Action</h3>

                                {
                                    setValueSection.actions.length > 0 && setValueSection.actions.map((action: IAction, actionIndex: number) => {
                                        const layoutFields = layoutFieldsConfigCtrl.getLayoutFields(props.selectedTemplateName);

                                        {/* <!-- single row --> */ }
                                        return (
                                            <React.Fragment key={actionIndex}>
                                                <div key={actionIndex} className="slds-grid slds-grid_vertical-align-center slds-gutters_x-small full-width rules-set">
                                                    <div className="slds-col col-sm slds-m-bottom_small">{actionIndex + 1}</div>
                                                    <div className="slds-col slds-size_4-of-12">
                                                        <div className="slds-form-element">
                                                            <div className="slds-form-element__control">
                                                                <div className="slds-select_container">
                                                                    <select role="select-action-field" className="slds-select" id="select-01"
                                                                        onChange={e => onActionFieldChanged(e, actionIndex, setValueSectionIndex, layoutFields)}
                                                                        value={action.field}>
                                                                        <option value="">Select action field</option>
                                                                        {
                                                                            layoutFields.
                                                                                map(field => <option key={field.fieldFullName}
                                                                                    value={field.fieldFullName}>{`${field.fieldLabel} - ${field.fieldFullName}`}
                                                                                </option>)
                                                                        }
                                                                    </select>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="slds-col slds-size_4-of-12">
                                                        {
                                                            !action.isDynamic && action.selectedFieldType !== 'Checkbox' &&
                                                            <div className="slds-form-element">
                                                                <div className="slds-form-element__control">
                                                                    <input role="input-action-value" id="" value={action.compareTo}
                                                                        type={getTextInputTypeBasedOnFieldType(action.selectedFieldType)}
                                                                        onChange={e => onActionValueChanged(e, actionIndex, setValueSectionIndex, false)}
                                                                        placeholder="Enter action value" className="slds-input" />
                                                                </div>
                                                            </div>
                                                        }
                                                        {
                                                            !action.isDynamic && action.selectedFieldType === 'Checkbox' &&
                                                            <label className="slds-checkbox_toggle" role="checkbox">
                                                                <input type="checkbox" name="checkbox-toggle-32" value="checkbox-toggle-32"
                                                                    aria-describedby="checkbox-toggle-32"
                                                                    onChange={e => onActionValueChanged(e, actionIndex, setValueSectionIndex, true)} />
                                                                <span id="checkbox-toggle-32" className="slds-checkbox_faux_container" aria-live="assertive">
                                                                    <span className="slds-checkbox_faux"></span>
                                                                    <span className="slds-checkbox_on">True</span>
                                                                    <span className="slds-checkbox_off">False</span>
                                                                </span>
                                                            </label>
                                                        }
                                                        {
                                                            action.isDynamic && action.compareTo === 'AddDays()' &&
                                                            <div className="slds-form-element ">
                                                                <div className="slds-form-element__control">
                                                                    <input id="" value={action.supportedFunctionValue}
                                                                        type="number"
                                                                        onChange={e => onActionSupportedFuncValueChanged(e, actionIndex, setValueSectionIndex)}
                                                                        placeholder="Enter value of AddDays() function" className="slds-input" />
                                                                </div>
                                                            </div>
                                                        }
                                                        {
                                                            action.isDynamic &&
                                                            <div className="slds-form-element">
                                                                <div className="slds-form-element__control">
                                                                    <div className="slds-select_container">
                                                                        <select role="select-dynamic-value" className="slds-select" id="select-01"
                                                                            value={action.compareTo}
                                                                            onChange={e => onActionValueChanged(e, actionIndex, setValueSectionIndex, false)}>
                                                                            <option value="">Select dynamic Value</option>
                                                                            {
                                                                                CQAppConstant.SETVALUE_RULE_DYNAMIC_SOURCE_VALUES.
                                                                                    map(field => <option key={field.label}
                                                                                        value={field.label}>{`${field.label}`}
                                                                                    </option>)
                                                                            }
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        }

                                                    </div>
                                                    <div className="slds-col slds-size_1-of-12">
                                                        <label className="slds-checkbox_toggle" role="checkbox">
                                                            <input role="input-action-dynamic" type="checkbox" name="checkbox-toggle-32" value="checkbox-toggle-32"
                                                                aria-describedby="checkbox-toggle-32"
                                                                checked={action.isDynamic}
                                                                onChange={e => onDynamicActionChanged(e, actionIndex, setValueSectionIndex)} />
                                                            <span id="checkbox-toggle-32" className="slds-checkbox_faux_container" aria-live="assertive">
                                                                <span className="slds-checkbox_faux"></span>
                                                                <span className="slds-checkbox_on">Dynamic</span>
                                                                <span className="slds-checkbox_off">Static</span>
                                                            </span>
                                                        </label>
                                                    </div>
                                                    <div className="slds-col">
                                                        <button role="add-action" className="slds-button slds-button_icon slds-button_icon-border-filled slds-m-horizontal_small" onClick={() => {
                                                            addAction(setValueSectionIndex);
                                                        }}>
                                                            <i className="cq-icon-add"></i>
                                                        </button>
                                                        {actionIndex !== 0 &&
                                                            <button className="slds-button slds-button_icon slds-button_icon-border-filled" onClick={() => {
                                                                deleteAction(actionIndex, setValueSectionIndex);
                                                            }}>
                                                                <i className="cq-icon-delete"></i>
                                                            </button>
                                                        }
                                                    </div>
                                                </div>
                                            </React.Fragment>
                                        );
                                    })
                                }
                                <div className="slds-border_bottom slds-m-vertical_small"></div>
                            </React.Fragment>)
                        })
                    }
                    <div className="slds-grid_absolute-center">
                        <button role="addition-rule" className="slds-button slds-button_neutral slds-m-top_x-small"
                            onClick={addSetValueSection}
                        >Add Additional Rule</button>
                    </div>
            </div>
        </>
    );
}
export const SetValuesRulesModal = observer(SetValuesRulesModalComponent);
